import React, {
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useState,
} from 'react'
import * as d3 from 'd3'
import R from 'ramda'
import MarginAnalysisContext from 'marginanalysis/MarginAnalysisContext'

export default ({ columnWidth = 100, data, dateHeaders, xScale, ...props }) => {
  const {
    showMaxEco,
    showMinEco,
    showTarget,
    scrollValueLeft,
    showGenMW,
  } = useContext(MarginAnalysisContext)

  const [maxEcoValue, setMaxEcoValue] = useState(0)
  const [minEcoValue, setMinEcoValue] = useState(0)

  const height = 150
  const heightBottomMargin = 2
  const margin = 50

  const filterDataWothoutItem = useCallback(obj => {
    // eslint-disable-next-line no-unused-vars
    const { item, ...vl } = obj
    return Object.values(vl).map(v => (isNaN(v) ? 0 : parseInt(v)))
  }, [])

  useEffect(
    _ => {
      const maxEcoList = data.find(c => c.item === 'ecoMax')
      if (maxEcoList === undefined) {
        setMaxEcoValue(10)
      } else {
        const maxEcoValue = R.reduce(
          (a, b) => (a < b ? b : a),
          0,
          filterDataWothoutItem(maxEcoList)
        )
        setMaxEcoValue(maxEcoValue)
      }
      const minEcoList = data.find(c => c.item === 'ecoMin')
      if (minEcoList === undefined) setMinEcoValue(0)
      else {
        const minEcoValue = R.reduce(
          R.minBy(a => a),
          Infinity,
          filterDataWothoutItem(minEcoList)
        )

        setMinEcoValue(minEcoValue)
      }
    },
    [data, filterDataWothoutItem]
  )

  const yScaleDomainVal = useMemo(
    _ => {
      return maxEcoValue + parseInt(maxEcoValue / 10)
    },
    [maxEcoValue]
  )
  const yScaleLiner = useMemo(
    _ => {
      const scale = d3
        .scaleLinear()
        .range([height - heightBottomMargin, 0])
        // .domain([0, 100])
        // .domain([minEcoValue, maxEcoValue])
        .domain([
          // minEcoValue - parseInt(minEcoValue / 5),
          0,
          yScaleDomainVal,
        ])

      return scale
    },
    [yScaleDomainVal]
  )

  const drawMaxEcoPath = useCallback(
    (svg, scrollValueLeft) => {
      svg
        .append('text')
        .text('Eco Max@' + maxEcoValue)
        .attr('class', 'ecolabel')
        .attr('x', scrollValueLeft)
        .attr('y', yScaleLiner(maxEcoValue))

      if (showMaxEco) {
        svg
          .append('line')
          .attr('class', 'ecoline')
          .attr('x1', margin)
          .attr('x2', columnWidth * dateHeaders.length)
          .attr('y1', yScaleLiner(maxEcoValue))
          .attr('y2', yScaleLiner(maxEcoValue))
          .style('stroke', 'rgb(26,147,108)')
          .style('strock-width', 2)
      }
    },
    [columnWidth, dateHeaders.length, maxEcoValue, showMaxEco, yScaleLiner]
  )

  const drawReductionRectPinkAvailMwMaxEco = useCallback(
    svg => {
      svg.selectAll('.reduction').remove()
      const ecoMax = data.find(c => c.item === 'ecoMax')
      const availMw = data.find(c => c.item === 'availMw')

      if (ecoMax && availMw) {
        let dv = dateHeaders
          .filter(k => ecoMax[k] && availMw[k])
          .map(k => ({
            k,
            e: (ecoMax[k] && parseInt(ecoMax[k])) || 0,
            a: (availMw[k] && parseInt(availMw[k])) || 0,
          }))
          .map(k => ({
            ...k,
            diff: k.e - k.a,
          }))

        dv = dv.filter(
          v => v.e && v.a >= 0 && v.diff && v.a < v.e && v.diff > 0
        )

        svg
          .append('g')
          .attr('class', 'reduction')
          .selectAll('.reduction')
          .data(dv)
          .enter()
          .append('rect')
          .attr('x', obj => xScale(obj.k))
          .attr('y', _ => yScaleLiner(maxEcoValue))
          // .attr('y', _ => yScaleLiner(yScaleDomainVal))
          .attr('width', 100)
          .attr('height', v => {
            // const val = yScaleLiner(height) - yScaleLiner(v.diff)
            // const val = height - v.diff
            // const val = v.diff
            // const val = height - heightBottomMargin - v.diff
            // const val = yScaleLiner(v.a)
            // const val=maxEcoValue-v.a
            // const val = yScaleLiner(maxEcoValue) - yScaleLiner(v.diff)
            const topHigh =
              yScaleLiner(yScaleDomainVal) - yScaleLiner(maxEcoValue)
            const val = yScaleLiner(v.a) + topHigh
            return val
          })
          .attr('fill', 'rgb(255, 186, 190)')
        // .attr('fill', 'pink')
      }
    },
    [data, dateHeaders, maxEcoValue, xScale, yScaleDomainVal, yScaleLiner]
  )

  const drawTargetMW = useCallback(
    svg => {
      svg.selectAll('.targetMw').remove()
      if (!showTarget) return
      const mwList = data.find(c => c.item === 'targetMw')
      if (mwList === undefined) return
      // const { item, ...mwProps } = mwList
      // const keys = Object.keys(mwProps)
      // let dv = keys.map(k => ({ x: k, y: mwProps[k] || '-1' }))

      const dv = dateHeaders.map(k => ({ x: k, y: mwList[k] || '-1' }))

      const lineFunc = d3
        .line()
        .x(xy => {
          const x = xScale(xy.x)

          // return x + columnWidth
          return x
        })
        .y(xy => {
          let val = parseInt(xy.y)
          val = val < 0 ? 0 : val
          return yScaleLiner(val)
        })
        .curve(d3.curveLinear)

      svg
        .append('path')
        .datum(dv)
        .attr('class', 'targetMw')
        .attr('stroke', 'rgb(153,26,56)')
        .attr('stroke-width', 2)
        .attr('fill', 'none')
        .style('stroke-dasharray', '3, 3')
        .attr('d', lineFunc)
    },
    [data, showTarget, dateHeaders, xScale, yScaleLiner]
  )

  const drawGenMW = useCallback(
    svg => {
      svg.selectAll('.genMw').remove()
      if (!showGenMW) return
      const mwList = data.find(c => c.item === 'genMw')
      if (mwList === undefined) return
      // const { item, ...mwProps } = mwList
      // const keys = Object.keys(mwProps)
      // let dv = keys.map(k => ({ x: k, y: mwProps[k] || '-1' }))

      const dv = dateHeaders.map(k => ({ x: k, y: mwList[k] || '-1' }))

      const lineFunc = d3
        .line()
        .x(xy => {
          const x = xScale(xy.x)

          // return x + columnWidth
          return x
        })
        .y(xy => {
          let val = parseInt(xy.y)
          val = val < 0 ? 0 : val
          return yScaleLiner(val)
        })
        .curve(d3.curveLinear)

      svg
        .append('path')
        .datum(dv)
        .attr('class', 'genMw')
        .attr('stroke', 'rgb(177, 93, 200)')
        .attr('stroke-width', 2)
        .attr('fill', 'none')
        .attr('d', lineFunc)
    },
    [data, showGenMW, dateHeaders, xScale, yScaleLiner]
  )

  const drawMinEcoPath = useCallback(
    svg => {
      if (showMinEco) {
        svg
          .append('line')
          .attr('class', 'ecoline')
          .attr('x1', margin)
          .attr('x2', columnWidth * dateHeaders.length)
          .attr('y1', yScaleLiner(minEcoValue))
          .attr('y2', yScaleLiner(minEcoValue))
          .style('stroke', 'rgb(26,147,108)')
          .style('strock-width', 2)
        svg
          .append('text')
          .text('Eco Min@' + minEcoValue)
          .attr('class', 'ecolabel')
          .attr('x', scrollValueLeft)
          .attr('y', yScaleLiner(minEcoValue))
      }
    },
    [
      columnWidth,
      dateHeaders.length,
      minEcoValue,
      scrollValueLeft,
      showMinEco,
      yScaleLiner,
    ]
  )

  const drawRSEVentTypes = useCallback(
    svg => {
      svg.selectAll('.rsevents').remove()
      const rsEvents = data.find(c => c.item === 'eventtype')
      if (rsEvents) {
        const dv = dateHeaders
          .map(k => ({
            k,
            e: rsEvents[k],
          }))
          .filter(de => de.e === 'RS')

        svg
          .append('g')
          .attr('class', 'rsevents')
          .selectAll('.rsevents')
          .data(dv)
          .enter()
          .append('rect')
          .attr('x', v => xScale(v.k))
          .attr('y', _ => yScaleLiner(yScaleDomainVal))
          .attr('width', 100)
          .attr('height', yScaleLiner(0))
          .attr('fill', 'rgb(220, 220, 220)')
      }
    },
    [data, dateHeaders, xScale, yScaleDomainVal, yScaleLiner]
  )

  useEffect(
    _ => {
      const svg = d3.select('#marginhourlygraph')
      svg.selectAll('*').remove()
      svg.selectAll('ecolabel').remove()
      svg.selectAll('rsevents').remove()

      // svg
      //   .append('g')
      //   .attr('transform', `translate(${50}, ${height - heightBottomMargin})`)
      //   .call(
      //     d3.axisBottom(xScale)
      //     // .ticks(days)
      //   )

      drawRSEVentTypes(svg)
      drawReductionRectPinkAvailMwMaxEco(svg)
      drawMinEcoPath(svg)
      drawMaxEcoPath(svg, scrollValueLeft)
      drawTargetMW(svg)
      drawGenMW(svg)
    },
    [
      columnWidth,
      dateHeaders.length,
      drawMaxEcoPath,
      scrollValueLeft,
      showMaxEco,
      showMinEco,
      xScale,
      yScaleLiner,
      drawTargetMW,
      drawGenMW,
      drawMinEcoPath,
      drawReductionRectPinkAvailMwMaxEco,
      drawRSEVentTypes,
    ]
  )

  // useEffect(
  //   _ => {
  //     drawGraph()
  //   },
  //   [drawGraph, scrollValueLeft]
  // )

  return (
    <svg
      ref={props.cref}
      id="marginhourlygraph"
      style={{
        backgroundColor: 'rgb(216, 248, 220)',
        width: columnWidth * (dateHeaders.length + 0) + 'px',
        height: height + 'px',
        // maxWidth: width,
      }}
    />
  )
}
